<template>
  <div id="MeteorShower"></div>
</template>

<script setup>
import p5 from 'p5'
import {ref, reactive, watchEffect, onUnmounted, watch, onMounted, toRaw} from 'vue'
import defaultValue from "@/utils/defaultValue"
import colorTool from "@/utils/colorTool"
import freeImg from "@/utils/freeImg"
const {freeImages} = freeImg()
const {defaultWidth, defaultHeight} = defaultValue()
const {rgbToHsb, changeToRgb} = colorTool()

const props = defineProps(['currentValue', 'effectTheme', 'controlMode'])

const effectThemeData = reactive(props.effectTheme)
let canvasWrap = ref('stop')
let nowTemperature = ref(null)
let animateSpeed = ref(2)
let backgroundHsbColor = ref([0,0,0])
let backgroundColor = ref('rgb(0,0,0)')
let imgUrl = ref(null)
let imagePathType = ref('')
let backgroundImage = ref('')
let currentColorArr = reactive({arr: []})
let high = ref(null)
let low = ref(null)
let colorLow = ref([])
let colorHigh = ref([])
let drawPot = ref(0)


const handleGetAnimateHSBAndSpeed = (high, low, now = 10, colorHigh, colorLow) => {

  // 高低差 range
  let lowHeightRange = Math.ceil((high - low) / 9) // 將高低差 分為 10個單位, 1單位


  let lowHeightColorRange

  let range = Math.trunc((now - low) / lowHeightRange)

  // 設定速度
  if (range <= 3) {
    animateSpeed.value = 2
    lowHeightColorRange = 1
  }
  else if (range >= 7 ) {
    animateSpeed.value = 4
    lowHeightColorRange = 10
  }
  else {
    animateSpeed.value = 3
    lowHeightColorRange = range
  }


  if (lowHeightColorRange === 1 ) {
    currentColorArr.arr = colorLow.map(color => rgbToHsb(color))
  }
  else if (lowHeightColorRange >= 10) {
    currentColorArr.arr = colorHigh.map(color => rgbToHsb(color))
  }
  else {





    const colorHighOne = rgbToHsb(colorHigh[0])
    const colorHighTwo = rgbToHsb(colorHigh[1])

    const colorLowOne = rgbToHsb(colorLow[0])
    const colorLowTwo = rgbToHsb(colorLow[1])

    const nowColorOne = [
      (lowHeightColorRange * colorHighOne[0] + (10 - lowHeightColorRange) * colorLowOne[0]) / 10,
      (lowHeightColorRange * colorHighOne[1] + (10 - lowHeightColorRange) * colorLowOne[1]) / 10,
      (lowHeightColorRange * colorHighOne[2] + (10 - lowHeightColorRange) * colorLowOne[2]) / 10,
    ]
    const nowColorTwo = [
      (lowHeightColorRange * colorHighTwo[0] + (10 - lowHeightColorRange) * colorLowTwo[0]) / 10,
      (lowHeightColorRange * colorHighTwo[1] + (10 - lowHeightColorRange) * colorLowTwo[1]) / 10,
      (lowHeightColorRange * colorHighTwo[2] + (10 - lowHeightColorRange) * colorLowTwo[2]) / 10,
    ]

    currentColorArr.arr = [
      nowColorOne,
      nowColorTwo
    ]

    if (
        colorHighOne[0] === colorLowOne[0] &&
        colorHighOne[1] === colorLowOne[1] &&
        colorHighOne[2] === colorLowOne[2]
    ) {
      currentColorArr.arr[0] = colorHighOne
    }
    if (
        colorHighTwo[0] === colorLowTwo[0] &&
        colorHighTwo[1] === colorLowTwo[1] &&
        colorHighTwo[2] === colorLowTwo[2]
    ) {
      currentColorArr.arr[1] = colorHighTwo
    }

  }
}


function initAnimate () {
  if (canvasWrap.value !== 'stop') {
    canvasWrap.value.remove()
    canvasWrap.value = 'stop'
  }
}


const meteorShowerAnimate = (sketch) => {
  let cw = defaultWidth.value
  let ch = defaultHeight.value
  let bg
  let vid
  let meteors = [];
  let backgroundAlpha = 30 // 控制 軌跡長短


  class meteorsDrop {
    constructor (x, y, long, index) {
      this.x = x;
      this.y = y;
      this.long = long;
      this.speed = 5;
      this.angle = 0;
      this.index = index
    }

    display (color ,x1, y1) {
      let displayX1 = x1 || this.x
      let displayY1 = y1 || this.y
      let displayX2 = x1 ? x1+this.long*sketch.sin(this.angle) : this.x+this.long*sketch.sin(this.angle)
      let displayY2 = y1 ? y1+this.long*sketch.cos(this.angle) : this.y+this.long*sketch.cos(this.angle)
      sketch.strokeWeight(1.8);
      sketch.push();
      sketch.stroke(...color);
      sketch.line(displayX1, displayY1, displayX2, displayY2);
      sketch.pop();
    }

    fall (speed) {
      this.angle = sketch.map(-300, -300, 300, -sketch.PI / 8, sketch.PI / 8, 0);
      this.y += this.speed * sketch.cos(this.angle) * 0.03;
      this.x += this.speed * sketch.sin(this.angle) * 0.03;
      this.speed += speed;
    }

    reset () {
      this.x = sketch.random(-sketch.width, sketch.width*2);
      this.y = sketch.random(-sketch.height*3, -sketch.height/2);
      this.speed = 5;
    }
  }

  sketch.setup = function () {
    sketch.createCanvas(cw, ch);
    sketch.colorMode(sketch.HSB, 360, 100, 100, 255)
    sketch.background(...backgroundHsbColor.value, backgroundAlpha)

    bg = ''
    vid = ''
    if (
        imagePathType.value !== 'gif' &&
        imagePathType.value !== 'mp4' &&
        imagePathType.value !== 'webm' &&
        imgUrl.value &&
        imgUrl.value !== null &&
        imgUrl.value !== ''
    ) bg = sketch.loadImage(imgUrl.value)
    // 若 imgUrl.value .gif
    if (imagePathType.value === 'gif' && imgUrl.value && imgUrl.value !== null && imgUrl.value !== '') {
      bg = sketch.createImg(imgUrl.value)
      bg.style('background-color', `${backgroundColor.value}`)
      bg.style('z-index', '-1')
      // 設置 背景圖 position
      bg.size(cw, ch)
      bg.position(0,0)
      // 將 canvas background 切換為 透明
      sketch.background(255, 0)
    }
    // 若 imgUrl.value .mp4 or .webm
    if (
        imagePathType.value === 'mp4' && imgUrl.value && imgUrl.value !== null && imgUrl.value !== '' ||
        imagePathType.value === 'webm' && imgUrl.value && imgUrl.value !== null && imgUrl.value !== ''
    ) {
      // mp4
      vid = sketch.createVideo(imgUrl.value)
      vid.style('background-color', `${backgroundColor.value}`)
      vid.style('z-index', '-1')
      // mp4
      vid.size(sketch.width, sketch.height);
      vid.position(0,0)
      vid.volume(0);
      vid.loop()
      sketch.background(255, 0)
    }

    // animate
    meteors = []
    for (let i = 0; i < 500; i++) {
      meteors.push(new meteorsDrop(
          sketch.random(-sketch.width,sketch.width*2),
          sketch.random(-sketch.height*3, -sketch.height/2),
          100 + sketch.random(-15, 15),
          i
      ));
    }
  }

  sketch.draw = function() {
    drawPot.value++
    if ( imagePathType.value === 'gif' ||
        imagePathType.value === 'mp4' ||
        imagePathType.value === 'webm'
    ) sketch.clear()

    if (imagePathType.value !== 'gif' &&
        imagePathType.value !== 'mp4' &&
        imagePathType.value !== 'webm') {
      if (
          imgUrl.value &&
          imgUrl.value !== null &&
          imgUrl.value !== ''
      ) {
        sketch.background(0, backgroundAlpha - 15)
        sketch.image(bg, 0, 0, sketch.width, sketch.height)
        // image alpha
        sketch.tint(255, backgroundAlpha - 15)
      } else {
        // 添加 背景色
        sketch.background(...backgroundHsbColor.value, backgroundAlpha)
      }
    }

    // animate
    for (let m of meteors) {
      let color = m.index % 2 === 0 ? currentColorArr.arr[0] : currentColorArr.arr[1]
      m.display(color);
      m.fall(sketch.float(animateSpeed.value));
      if (sketch.float(animateSpeed.value) > 2) {
        m.display(
            color,
            sketch.random(-sketch.width/2,sketch.width),
            sketch.random(-sketch.height*3, -sketch.height/2)
        );
        m.fall(sketch.float(animateSpeed.value * 0.005));
      }
      if (sketch.float(animateSpeed.value) > 3) {
        m.display(
            color,
            sketch.random(-sketch.width/4,sketch.width/2),
            sketch.random(-sketch.height*3, -sketch.height/2)
        );
        m.fall(sketch.float(animateSpeed.value * 0.0001));
      }

      if (m.y >= sketch.height) {
        m.reset();
      }
    }

    if (drawPot.value > 7200) {
      // animate
      meteors = meteors.filter(m => m.index < sketch.random(100))
      for (let i = 0; i < 500; i++) {
        meteors.push(new meteorsDrop(
            sketch.random(-sketch.width,sketch.width*2),
            sketch.random(-sketch.height*3, -sketch.height/2),
            100 + sketch.random(-15, 15),
            i
        ));
      }
      drawPot.value = 0
    }
  }
}

watch(() => effectThemeData.Sensor, (Sensor) => {
  if (Sensor) {
    initAnimate()
    canvasWrap.value = new p5(meteorShowerAnimate, 'MeteorShower')
  }
})

watchEffect(() => {
  const {
    HighValue,
    LowValue,
    IsDynamicMode,
    Speed: rotateSpeed,
    BackgroundColor,
    BackgroundImage,
    HighColors,
    LowColors,
    Colors
  } = effectThemeData

  // 背景色
  let bgColor = typeof BackgroundColor === "object" && BackgroundColor.length > 0 ? changeToRgb(BackgroundColor) : 'rgb(0,0,0)'
  let bgHsbColor = typeof BackgroundColor === "object" && BackgroundColor.length > 0 ? rgbToHsb(BackgroundColor) : [0,0,0]

  // 背景色, 背景圖 需 重置動畫
  if (
      backgroundImage.value !== BackgroundImage ||
      backgroundColor.value !== bgColor ||
      backgroundHsbColor.value[0] !== bgHsbColor[0] ||
      backgroundHsbColor.value[1] !== bgHsbColor[1] ||
      backgroundHsbColor.value[2] !== bgHsbColor[2]
  ) {
    backgroundHsbColor.value = bgHsbColor
    backgroundColor.value = bgColor
    imagePathType.value = ''
    backgroundImage.value = BackgroundImage
    if (backgroundImage.value !== '') {
      if (props.controlMode && props.controlMode.toString() === 'true') {
        const pathSplit = backgroundImage.value.split('.')
        imgUrl.value = freeImages[pathSplit[0]]
        imagePathType.value = pathSplit[1]
      } else {
        imgUrl.value = backgroundImage.value.split('#')[0]
        if (imgUrl.value.endsWith('.mp4')) imagePathType.value = 'mp4'
        else if (imgUrl.value.endsWith('.webm')) imagePathType.value = 'webm'
        else if (imgUrl.value.endsWith('.gif')) imagePathType.value = 'gif'
      }
    }
    if (backgroundImage.value === '') {
      imgUrl.value = ''
      imagePathType.value = ''
    }
    // create p5
    if (canvasWrap.value !== 'stop') {
      initAnimate()
      canvasWrap.value = new p5(meteorShowerAnimate, 'MeteorShower')
    }
  }


  // animateSpeed 可以動態更新
  nowTemperature.value = props.currentValue && props.currentValue !== null ? props.currentValue : ''
  if (IsDynamicMode.toString() === 'false') {
    currentColorArr.arr = Colors.map(color => rgbToHsb(color))
    if (rotateSpeed && rotateSpeed > 0) animateSpeed.value = rotateSpeed
  }
  else if (IsDynamicMode.toString() === 'true') {
    high.value = HighValue
    low.value = LowValue
    colorHigh.value = HighColors
    colorLow.value = LowColors
    handleGetAnimateHSBAndSpeed(high.value, low.value, nowTemperature.value, colorHigh.value, colorLow.value)
  }

})

onMounted(() => {
  if (canvasWrap.value === 'stop') canvasWrap.value = new p5(meteorShowerAnimate, 'MeteorShower')
})

onUnmounted(() => {
  drawPot.value = 0
  nowTemperature.value = null
  animateSpeed.value = 2
  backgroundHsbColor.value = [0,0,0]
  backgroundColor.value = 'rgb(0,0,0)'
  imgUrl.value = ''
  imagePathType.value = ''
  backgroundImage.value = ''
  currentColorArr.arr = []
  high.value = null
  low.value = null
  colorHigh.value = []
  colorLow.value = []
  initAnimate()
})

</script>
